/**
 * Detalle del reporte "Informe mensual a los vecinos" (2026-09-03).
 *
 * Es el único reporte que además de mostrarse SE ENVÍA, así que la pantalla
 * tiene dos trabajos:
 *
 * 1. **Mostrar el correo tal como le llega al vecino.** El HTML lo renderiza
 *    el backend y acá se pinta dentro de un iframe. No es un detalle técnico:
 *    si la pantalla lo re-maquetara con los componentes del portal, el
 *    tesorero aprobaría algo distinto de lo que sale. El iframe además aísla
 *    el CSS claro del correo del tema oscuro del portal.
 * 2. **Hacer visible el riesgo antes de apretar.** A cuántos titulares va, qué
 *    casas quedan fuera, y si el informe de este período ya salió. El envío
 *    real pide confirmación; la prueba va solo al correo del tesorero.
 *
 * Recibe los datos y las funciones de envío por props para poder validarse en
 * el preview dev-only sin tocar red.
 */
import { useState } from 'react';
import type {
  InformeMensualVecinosResponse,
  ResultadoEnvioInforme,
  ResultadoEnvioPrueba,
} from '../../lib/api/reportes-tesoreria';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ModalConfirmar } from '../ui/ModalConfirmar';
import { CasasFuera, VistaPreviaCorreo } from './EnvioMasivo';
import { fechaCorta } from '../../lib/tiempo';

function formatCLP(n: number): string {
  return `$${n.toLocaleString('es-CL')}`;
}

export interface InformeMensualVecinosProps {
  informe: InformeMensualVecinosResponse;
  onEnviarPrueba: () => Promise<ResultadoEnvioPrueba>;
  onEnviar: () => Promise<ResultadoEnvioInforme>;
  /** Se llama tras un envío real para que el contenedor recargue el estado. */
  onEnviado?: () => void;
}

export function InformeMensualVecinos({
  informe,
  onEnviarPrueba,
  onEnviar,
  onEnviado,
}: InformeMensualVecinosProps) {
  const [enviando, setEnviando] = useState<'prueba' | 'real' | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  const d = informe.destinatarios;
  const p = informe.proyeccion;
  const c = informe.cierre;
  const pendientes = d.titulares_con_correo - d.ya_enviados;
  const yaSalioCompleto = pendientes <= 0 && d.ya_enviados > 0;
  // El backend rechaza el envío real con 409 si el seguro está puesto; la
  // pantalla no debe ofrecer un botón que va a fallar.
  const puedeEnviar = informe.envio_habilitado && pendientes > 0;

  async function enviarPrueba() {
    setEnviando('prueba');
    setError(null);
    setAviso(null);
    try {
      const r = await onEnviarPrueba();
      setAviso(
        r.ok
          ? `Prueba enviada a ${r.enviado_a}. El envío a los titulares sigue pendiente.`
          : `No se pudo enviar la prueba a ${r.enviado_a}.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo enviar la prueba.');
    } finally {
      setEnviando(null);
    }
  }

  async function enviarReal() {
    setEnviando('real');
    setError(null);
    setAviso(null);
    try {
      const r = await onEnviar();
      const partes = [`Enviado a ${r.enviados} titular(es).`];
      if (r.omitidos_ya_enviados > 0) {
        partes.push(`${r.omitidos_ya_enviados} ya lo tenían.`);
      }
      if (r.fallidos.length > 0) {
        partes.push(
          `Falló en ${r.fallidos.length}: casas ${r.fallidos.join(', ')}. ` +
            'Puedes volver a enviar: el reintento alcanza solo a esas.',
        );
      }
      setAviso(partes.join(' '));
      onEnviado?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo enviar el informe.');
    } finally {
      setEnviando(null);
    }
  }

  return (
    <div className="space-y-4">
      {error && (
        <div
          role="alert"
          className="bg-state-danger/10 border-[0.5px] border-state-danger/30 text-state-danger rounded-md p-3 text-sm"
        >
          {error}
        </div>
      )}
      {aviso && (
        <div className="bg-state-success/10 border-[0.5px] border-state-success/30 text-state-success rounded-md p-3 text-sm">
          {aviso}
        </div>
      )}

      {/* ── Qué dice el informe ── */}
      <Card title={`Cierre de ${informe.periodo}`}>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-text-secondary text-xs">Ingresos</dt>
            <dd className="text-xl mt-0.5">{formatCLP(c.ingresos)}</dd>
          </div>
          <div>
            <dt className="text-text-secondary text-xs">Gastos</dt>
            <dd className="text-xl mt-0.5">{formatCLP(c.gastos)}</dd>
          </div>
          <div>
            <dt className="text-text-secondary text-xs">Resultado</dt>
            <dd
              className="text-xl mt-0.5"
              style={{ color: c.resultado >= 0 ? undefined : '#ff7a7a' }}
            >
              {formatCLP(c.resultado)}
            </dd>
          </div>
          <div>
            {/* Con la fecha REAL del snapshot, igual que el correo. Que el
                panel diga "al cierre" y el correo "al 27 ago" haría que el
                tesorero apruebe algo distinto de lo que revisó. */}
            <dt className="text-text-secondary text-xs">
              {c.saldo_cierre_fecha
                ? `Saldo al ${fechaCorta(c.saldo_cierre_fecha)}`
                : 'Saldo en el banco'}
            </dt>
            <dd className="text-xl mt-0.5">
              {c.saldo_cierre === null ? '—' : formatCLP(c.saldo_cierre)}
            </dd>
          </div>
        </dl>
      </Card>

      {/* ── El bloque que da sentido al saldo ── */}
      <Card title="Cuánto alcanza ese saldo">
        {informe.cobertura_meses === null ? (
          <p className="text-sm text-text-secondary">
            Sin saldo bancario o sin gasto conocido en la ventana, no se puede
            calcular la cobertura. El informe omite este bloque.
          </p>
        ) : (
          <>
            <p className="text-sm">
              Gasto mensual promedio de los últimos {p.meses_ventana} meses:{' '}
              <strong>{formatCLP(p.promedio)}</strong>. El saldo alcanza para{' '}
              <strong>
                {informe.cobertura_meses.toLocaleString('es-CL')} meses
              </strong>
              .
            </p>
            <p className="text-xs text-text-secondary mt-2">
              Ventana {p.desde} → {p.hasta}. Mes más bajo{' '}
              {formatCLP(p.mes_minimo)}, más alto {formatCLP(p.mes_maximo)}.
              {p.usa_historico &&
                ' Incluye meses previos a que el sistema operara, tomados del histórico cargado.'}
              {p.meses_sin_datos > 0 &&
                ` ${p.meses_sin_datos} mes(es) de la ventana sin datos en ninguna fuente: el promedio queda por debajo del real.`}
            </p>
          </>
        )}
      </Card>

      {/* ── A quién le llega ── */}
      <Card title="A quién se envía">
        <p className="text-sm">
          <strong>{d.titulares_con_correo}</strong> de {d.total_casas} casas
          tienen titular con correo registrado.
          {d.ya_enviados > 0 && (
            <>
              {' '}
              Ya recibieron el informe de {informe.periodo}:{' '}
              <strong>{d.ya_enviados}</strong>.
            </>
          )}
        </p>
        <CasasFuera
          identificadores={d.identificadores_sin_correo}
          resumen={`${d.casas_sin_correo} casas no reciben el informe (sin correo)`}
        />
      </Card>

      {/* ── El correo como lo ve el vecino ── */}
      <Card title="Vista del correo">
        <VistaPreviaCorreo asunto={informe.asunto} html={informe.html} />
      </Card>

      {/* ── Envío ── */}
      <Card title="Enviar">
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            variant="secondary"
            onClick={() => void enviarPrueba()}
            disabled={enviando !== null}
          >
            {enviando === 'prueba' ? 'Enviando…' : 'Enviar prueba a mi correo'}
          </Button>
          <Button
            variant="primary"
            onClick={() => setConfirmando(true)}
            disabled={enviando !== null || !puedeEnviar}
          >
            {enviando === 'real'
              ? 'Enviando…'
              : `Enviar a ${pendientes} titular(es)`}
          </Button>
        </div>
        {!informe.envio_habilitado && (
          <p className="text-sm text-state-warning mt-2">
            <strong>El envío a los titulares está desactivado.</strong> La
            prueba a tu correo sigue disponible: úsala para revisar el informe.
            Para habilitar el envío real hay que cambiar la configuración del
            servidor.
          </p>
        )}
        <p className="text-xs text-text-secondary mt-2">
          {yaSalioCompleto
            ? `El informe de ${informe.periodo} ya salió a todos los titulares con correo.`
            : 'La prueba va solo a tu correo y no marca el período como enviado. ' +
              'El envío a los titulares no se puede deshacer.'}
        </p>
      </Card>

      {confirmando && (
        <ModalConfirmar
          title="Enviar el informe a los titulares"
          mensaje={
            `Se enviará el cierre de ${informe.periodo} a ${pendientes} ` +
            'titular(es). Un correo enviado no se puede deshacer. ' +
            'Si todavía no lo revisaste, cancela y usa "Enviar prueba a mi correo".'
          }
          textoConfirmar="Enviar ahora"
          onConfirmar={async () => {
            setConfirmando(false);
            await enviarReal();
          }}
          onCancelar={() => setConfirmando(false)}
        />
      )}
    </div>
  );
}
